import React, { useCallback, useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { apiService } from "../services/api";

interface SupportTicketFormProps {
  isOpen: boolean;
  onClose: () => void;
  source: "LANDING_PAGE" | "JOB_SEEKER_PORTAL" | "COMPANY_PORTAL";
  userInfo?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  initialCategory?: string;
}

const SupportTicketForm: React.FC<SupportTicketFormProps> = ({
  isOpen,
  onClose,
  source,
  userInfo,
  initialCategory = "GENERAL_INQUIRY",
}) => {
  const sanitizeDefaultValue = (value?: string) => {
    const normalized = value?.trim() || "";
    return /^undefined(\s+undefined)?$/i.test(normalized) ? "" : normalized;
  };

  const getDefaultFormData = useCallback(
    () => ({
      contact_name: sanitizeDefaultValue(userInfo?.name),
      contact_email: sanitizeDefaultValue(userInfo?.email),
      contact_phone: sanitizeDefaultValue(userInfo?.phone),
      subject: "",
      description: "",
      category: initialCategory,
      priority: "MEDIUM",
    }),
    [userInfo?.name, userInfo?.email, userInfo?.phone, initialCategory],
  );
  console.log(
    "Initializing SupportTicketForm with userInfo:",
    userInfo,
    "and initialCategory:",
    initialCategory,
  );
  const [formData, setFormData] = useState({
    ...getDefaultFormData(),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(getDefaultFormData());
    }
  }, [isOpen, getDefaultFormData]);

  const categories = [
    { value: "GENERAL_INQUIRY", label: "General Inquiry" },
    { value: "TECHNICAL_SUPPORT", label: "Technical Support" },
    { value: "BILLING_SUPPORT", label: "Billing Support" },
    { value: "ACCOUNT_ISSUE", label: "Account Issue" },
    { value: "PRICING_QUESTION", label: "Pricing Question" },
    { value: "OTHER", label: "Other" },
  ];

  const priorities = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "URGENT", label: "Urgent" },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const updateField = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        subject: formData.subject,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        source: source,
      };

      console.log("Sending payload:", payload);
      const response = await apiService.post("/support-tickets/", payload);

      console.log("Response:", response.data);

      if (response.data) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
          setSubmitSuccess(false);
          setFormData(getDefaultFormData());
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      alert("Failed to submit support ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancel = () => {
    setFormData(getDefaultFormData());
    onClose();
  };

  if (!isOpen) return null;

  const getSourceTitle = () => {
    switch (source) {
      case "LANDING_PAGE":
        return "Contact Us";
      case "JOB_SEEKER_PORTAL":
        return "Get Support";
      case "COMPANY_PORTAL":
        return "Contact Support";
      default:
        return "Support Request";
    }
  };

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Support Ticket Submitted!
            </h3>
            <p className="text-sm text-gray-600">
              We've received your request and will get back to you soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {getSourceTitle()}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                type="text"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => {
                  const value = e.target.value;
                  const cleaned = value.replace(/[^0-9+\-()\s]/g, "");

                  updateField("contact_phone", cleaned);
                }}
                // onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            {/* Category and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of your request"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Please provide detailed information about your request or issue..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketForm;
